export type WooProductSummary = {
	id?: number;
	title: string;
	href: string;
	image: string;
	/** HTML já formatado pelo WooCommerce: moeda, promoção, faixa de variável e sufixo de IVA. */
	priceHtml?: string;
	onSale?: boolean;
	inStock?: boolean;
	/** Página do produto para variáveis/esgotados; `?add-to-cart=ID` para simples compráveis. */
	addToCartUrl?: string;
	addToCartText?: string;
	/** `false` para produtos variáveis/agrupados/esgotados: o botão vira um link comum. */
	isAjaxAddToCart?: boolean;
	/** HTML completo da descrição breve; para uma página de produto dedicada. */
	shortDescription?: string;
	/** Só a primeira linha, em texto puro — o suficiente para um cartão de grade. */
	shortDescriptionLine?: string;
};

export type CosaNovaWordPressConfig = {
	assetBase?: string;
	ajaxUrl?: string;
	contactNonce?: string;
	cartCount?: number;
	homeUrl?: string;
	cartUrl?: string;
	accountUrl?: string;
	basePath?: string;
	products?: Record<string, WooProductSummary>;
	shopProducts?: WooProductSummary[];
};

declare global {
	interface Window {
		COSANOVA_WP?: CosaNovaWordPressConfig;
		jQuery?: JQueryLike;
	}
}

/** Só o suficiente do jQuery para assinar o evento `added_to_cart` do WooCommerce. */
type JQueryLike = (target: unknown) => {
	on: (event: string, handler: (...args: unknown[]) => void) => void;
	off: (event: string, handler: (...args: unknown[]) => void) => void;
};

const config = (): CosaNovaWordPressConfig => window.COSANOVA_WP ?? {};

/** Distingue o protótipo local de uma página alimentada pelo WordPress. */
export function hasWordPressConfig(): boolean {
	return window.COSANOVA_WP !== undefined;
}

/**
 * Traduz apenas os assets locais do protótipo. Em desenvolvimento eles continuam
 * a sair de /assets; no WordPress apontam para a pasta app/assets do child theme.
 */
export function assetUrl(path: string): string {
	const prefix = "/assets/";
	const assetBase = config().assetBase?.replace(/\/$/, "");

	if (!assetBase || !path.startsWith(prefix)) return path;

	return `${assetBase}/${path.slice(prefix.length)}`;
}

export function initialCartCount(): number {
	const count = Number(config().cartCount ?? 0);
	return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
}

/** Mantém links internos dentro do domínio/ambiente WordPress atual. */
export function siteUrl(path: string): string {
	if (!path.startsWith("/")) return path;

	const homeUrl = config().homeUrl?.replace(/\/$/, "");
	return homeUrl ? `${homeUrl}${path}` : path;
}

export function cartUrl(): string {
	return config().cartUrl || siteUrl("/cistella/");
}

export function accountUrl(): string {
	return config().accountUrl || siteUrl("/el-meu-compte/");
}

/** Remove o subdiretório da instalação antes de resolver as rotas do React. */
export function currentSitePath(): string {
	const pathname = window.location.pathname || "/";
	const basePath = config().basePath?.replace(/\/$/, "") || "";

	if (!basePath || basePath === "/") return pathname;
	if (pathname === basePath) return "/";
	if (pathname.startsWith(`${basePath}/`)) return pathname.slice(basePath.length);

	return pathname;
}

/** Capa, título e link reais de um produto, quando o WooCommerce os fornece. */
export function wooProduct(slug: string): WooProductSummary | null {
	return config().products?.[slug] ?? null;
}

/** Catálogo completo e visível da loja, na ordem que o WooCommerce já define. */
export function wooShopProducts(): WooProductSummary[] {
	return config().shopProducts ?? [];
}

/**
 * Avisa quando um "Afegeix a la cistella" do WooCommerce completa com sucesso
 * (o próprio `wc-add-to-cart.js`, já carregado pelo WordPress, dispara esse
 * evento em `document.body`). Sem jQuery disponível, não faz nada — a conta do
 * carrinho no cabeçalho só fica desatualizada até a próxima navegação.
 *
 * O WooCommerce dispara `(event, fragments, cart_hash, $button)`; repassamos
 * o botão clicado para quem assina poder mostrar feedback só nele, não em
 * todos os produtos da página.
 */
export function onProductAddedToCart(
	handler: (button: HTMLElement | null) => void,
): () => void {
	const jQuery = window.jQuery;
	if (!jQuery) return () => {};

	const wrapped = (...args: unknown[]) => {
		const jqButton = args[3] as { get?: (index: number) => HTMLElement } | undefined;
		handler(jqButton?.get?.(0) ?? null);
	};
	const body = jQuery(document.body);
	body.on("added_to_cart", wrapped);
	return () => body.off("added_to_cart", wrapped);
}

export function wordpressContactConfig() {
	const { ajaxUrl, contactNonce } = config();

	return ajaxUrl && contactNonce ? { ajaxUrl, contactNonce } : null;
}
